/**
 * Chat workbook UI (Lane 1132, Plan A, WS7) — the quiet offer, the
 * plain-English plan card, and the durable job row.
 *
 * Owns the new runtime per critical-rules: Home.js COMPOSES these
 * components but is not the owner. Server state is authoritative
 * throughout — the browser never supplies a spec, an owner, or a
 * retry/revise decision (the server's `next_action` renders verbatim).
 *
 * The offer is SUBTLE by ruling: a small ghost action under the answer,
 * never a card. Accepting replaces it in place with busy text; the
 * streamed result becomes the plan card under the same answer. A failure
 * restores a Retry action; an expired token becomes "Ask again".
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { requestChatTurn } from '../utils/chatProgressStream';

/** Module-level announce-once registry: a terminal job announces exactly
 *  once per page lifetime, however many times polling/remount re-render
 *  it (stable identity `report-job:<id>:terminal`). */
const announcedTerminals = new Set();

/** Lane 1133: how long the browser waits for a stored plan after the accept
 *  stream is cut. The server's accept-control deadline is 420s (announced on
 *  the stream); this floor covers it with margin, and the announced value
 *  extends it when larger. Poll every 5s — the lookup is one indexed row. */
export const PLAN_RECOVERY_FLOOR_MS = 450_000;
export const PLAN_RECOVERY_POLL_MS = 5_000;

/** Lane 1141: the download route (routes/reports.py:327-334) emits exactly
 *  one header shape — `attachment; filename="<name>"` — where <name> has
 *  already been stripped server-side to [alnum . _ -] and truncated to 120
 *  characters, with its own `report.xlsx` fallback when nothing survives.
 *  Parse that one contract and nothing else: no RFC 5987 `filename*`, no
 *  path text, no name invented from the question. Anything that does not
 *  match — a missing header, a malformed one, an empty name — falls back to
 *  the same `report.xlsx` the server itself falls back to, so the download
 *  always works. */
const SERVER_FILENAME_RE = /attachment;\s*filename="([A-Za-z0-9._-]+)"/i;
const FALLBACK_FILENAME = 'report.xlsx';

export function serverFilename(response) {
  let header = null;
  try {
    header = response && response.headers && response.headers.get
      ? response.headers.get('Content-Disposition')
      : null;
  } catch (e) {
    header = null;
  }
  if (typeof header !== 'string') return FALLBACK_FILENAME;
  const match = SERVER_FILENAME_RE.exec(header);
  return match ? match[1] : FALLBACK_FILENAME;
}

/** Poll GET /api/report-plans for the accepted offer's completed turn.
 *  Resolves to a turn-shaped result so the caller treats it exactly like a
 *  streamed accept. Three honest exits: the server's persisted turn payload,
 *  a legacy completed response without that payload (resolves null so the
 *  utility replays it), or a typed failure
 *  (auth lost, or the deadline passed) -- never a silent success. The
 *  deadline is measured from the ACCEPT START, not from the cut, so a
 *  watchdog-fired truncation never waits for a turn the server already
 *  abandoned. */
export async function recoverStoredPlan({ authFetch, conversationId, offerId,
                                          startedAt, deadlineMs, onProgress,
                                          sleep }) {
  const began = typeof startedAt === 'number' ? startedAt : Date.now();
  const wait = sleep || ((ms) => new Promise((r) => { setTimeout(r, ms); }));
  const url = `/api/report-plans?conversation_id=${encodeURIComponent(conversationId)}`
    + `&offer_id=${encodeURIComponent(offerId)}`;
  for (;;) {
    try {
      const response = await authFetch(url);
      if (response && (response.status === 401 || response.status === 403)) {
        return { ok: false, status: response.status, data: null,
                 recovered: false,
                 errData: { detail: { code: 'plan_recovery_unauthorized',
                                      message: 'Your session ended before '
                                               + 'the plan could be '
                                               + 'recovered.' } } };
      }
      if (response && response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.turn_result) {
          return { ok: true, status: 200, recovered: true,
                   data: data.turn_result,
                   errData: null };
        }
        if (data.turn_complete === true) {
          return null; // finished without a plan: replay the stored answer
        }
      }
    } catch (transient) { /* a failed poll is not a verdict; keep waiting */ }
    if (Date.now() - began >= deadlineMs) {
      return { ok: false, status: 504, data: null, recovered: false,
               errData: { detail: { code: 'plan_recovery_timeout',
                                    message: 'The stored plan did not appear '
                                             + 'in time.' } } };
    }
    if (onProgress) onProgress();
    await wait(PLAN_RECOVERY_POLL_MS);
  }
}

export function ReportOfferAction({ offer, conversationId, authFetch,
                                    getHeaders, onPlanTurn, recoverySleep,
                                    autoStart = false, onAutoStarted = null,
                                    onAutoSettled = null }) {
  const [state, setState] = useState('idle'); // idle|busy|failed|expired
  // Live progress while the proposal turn runs (the accept is a full model
  // turn — 1-3 minutes — so it streams the same way a chat turn does; a
  // silent fetch would also die at the proxy's ~100s cutoff, measured live
  // on staging 2026-09-01).
  //
  // ⭐ Lane 1144 (C1): this is an ORDERED LIST, not one line that replaces
  // itself. Ryan watched a workbook get built and saw a single sentence
  // overwrite itself, and named the deck builder's narration as the thing
  // it should feel like. What is borrowed from that screen is its SHAPE —
  // an accumulating column on one continuous hairline rail, so the buyer
  // can see what the turn actually did rather than only where it is now.
  //
  // ⛔ What is NOT borrowed, deliberately: the deck's timed reveal
  // (`NARRATION_BEAT_MS`), its dry-run pre-plan, its veto and its seam.
  // Those exist because the deck's decisions are all settled BEFORE the
  // narration starts, so it can pace them. Nothing here is settled in
  // advance: a line appears at the instant the server emits it, and never
  // otherwise. And there are no checkmarks, percentages, counts or phase
  // names — the same reason Lane 177's client-side stepper was deleted.
  // Nothing in the browser observes a stage COMPLETING, so nothing here
  // may draw one (Rule 116: an invented figure is still invented when it
  // is drawn as a bar).
  //
  // Every entry is either a server `stage` line
  // (`chat_mirror_runtime.py` — `mirror_investigating` :1576,
  // `mirror_canonical` :1914, `report_proposal` :1872, and
  // `mirror_query` :2012, which numbers its own attempts so successive
  // queries are DIFFERENT text) or the client's own connection-drop
  // notice below.
  const [progressLines, setProgressLines] = useState([]);
  const headingRef = useRef(null);

  //: Append in receipt order, suppressing ONLY an exact consecutive
  //: duplicate. Two different query lines that share the `mirror_query`
  //: stage must both survive — that repetition is the texture that shows
  //: the turn working, and collapsing by STAGE would erase it.
  const appendProgress = (line) => {
    setProgressLines((prev) => (
      prev.length > 0 && prev[prev.length - 1] === line ? prev : [...prev, line]
    ));
  };

  const accept = async () => {
    setState('busy');
    setProgressLines([]);
    const body = {
      control: { type: 'accept_report_offer', offer_token: offer.token },
      conversation_id: conversationId,
      idempotency_key: offer.offer_id,
    };
    // The server announces its accept-control ceiling on the stream; the
    // recovery deadline follows it (never shorter than the floor) and is
    // measured from the accept START.
    let recoveryDeadlineMs = PLAN_RECOVERY_FLOOR_MS;
    const acceptStartedAt = Date.now();
    try {
      const headers = getHeaders
        ? getHeaders()
        : { 'Content-Type': 'application/json' };
      const turn = await requestChatTurn({
        streamUrl: `${API_BASE}/api/ai-assistant/stream`,
        fallbackUrl: `${API_BASE}/api/ai-assistant`,
        headers,
        body,
        onEvent: (event) => {
          if (event.type === 'accepted'
              && typeof event.deadline_seconds === 'number') {
            recoveryDeadlineMs = Math.max(
              recoveryDeadlineMs, (event.deadline_seconds + 30) * 1000);
          }
          if (event.type === 'stage' && event.line) {
            appendProgress(event.line);
          }
        },
        // Lane 1133: a cut stream never re-runs the model. The server
        // finishes the turn on its own and stores the plan; poll for it.
        onTruncated: () => {
          // The one line the CLIENT owns. It is appended, never substituted:
          // the stages that already happened still happened, and the server
          // is still finishing the turn behind the cut.
          appendProgress('Connection dropped. Waiting for the stored plan…');
          return recoverStoredPlan({
            authFetch, conversationId, offerId: offer.offer_id,
            startedAt: acceptStartedAt, deadlineMs: recoveryDeadlineMs,
            sleep: recoverySleep,
          });
        },
      });
      if (!turn.ok) {
        const code = turn.errData?.detail?.code || '';
        setState(code.startsWith('offer_token') ? 'expired' : 'failed');
        return;
      }
      onPlanTurn(turn.data);
    } catch (e) {
      setState('failed');
    }
  };


  // Lane 1405 v3: the Build a workbook button by the chat box starts the same
  // accept at once (one click), exactly once for this offer.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStart && !autoStarted.current && state === 'idle') {
      autoStarted.current = true;
      // The page forgets the request at once, so a remount never starts it again.
      if (onAutoStarted) onAutoStarted();
      // Success or not, the page learns when this start has ended.
      Promise.resolve(accept()).finally(() => { if (onAutoSettled) onAutoSettled(); });
    }
  }, [autoStart]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'busy') {
    return (
      <div className="hm-rptoffer hm-rptoffer-busy"
        data-testid="hm-report-offer-busy" aria-busy="true">
        <span className="hm-rptoffer-busyh">Preparing your workbook plan</span>
        {progressLines.length > 0 && (
          // `aria-live="polite"` with the default `aria-relevant` announces
          // the APPENDED line only, so a screen reader hears each new stage
          // once instead of the whole column re-read on every event.
          <ol className="hm-rptoffer-progress"
            data-testid="hm-report-offer-progress" aria-live="polite">
            {progressLines.map((line, i) => (
              // Append-only and never reordered, so the index is a stable
              // identity here; the line text disambiguates it besides.
              <li key={`${i}:${line}`}
                data-testid="hm-report-offer-progress-line">{line}</li>
            ))}
          </ol>
        )}
      </div>
    );
  }
  if (state === 'expired') {
    return (
      <div className="hm-rptoffer" data-testid="hm-report-offer-expired">
        This offer expired. Ask for the workbook again.
      </div>
    );
  }

  return (
    <div className="hm-rptoffer" ref={headingRef}>
      <button
        type="button"
        className="hm-rptoffer-btn"
        data-testid="hm-report-offer"
        onClick={accept}
      >
        {offer.label || 'Build your workbook'}
      </button>
      {state === 'failed' && (
        <span className="hm-rptoffer-fail" role="alert">
          That didn’t start.{' '}
          <button type="button" className="hm-rptoffer-retry"
            data-testid="hm-report-offer-retry" onClick={accept}>
            Retry
          </button>
        </span>
      )}
    </div>
  );
}

export function ReportPlanCard({ plan, authFetch, onQueued }) {
  const [state, setState] = useState('idle'); // idle|busy|superseded|failed
  const [failMessage, setFailMessage] = useState('');
  // ONE idempotency key per rendered card: a double-click replays the
  // same job server-side instead of creating a second one.
  const idempotencyKey = useRef(
    `build-${plan.plan_id}-${Math.random().toString(36).slice(2, 10)}`);
  const headingRef = useRef(null);

  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, []);

  const build = async () => {
    setState('busy');
    setFailMessage('');
    try {
      const response = await authFetch(
        `/api/report-plans/${plan.plan_id}/build`,
        { method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_version: plan.version,
                                 idempotency_key: idempotencyKey.current }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data?.detail?.code === 'plan_superseded') {
          setState('superseded');
          return;
        }
        setState('failed');
        setFailMessage(data?.detail?.message
          || 'The build could not be started.');
        return;
      }
      onQueued(data.job_id);
    } catch (e) {
      setState('failed');
      setFailMessage('The build could not be started.');
    }
  };

  return (
    <div className="hm-rptplan" data-testid="hm-report-plan">
      <div className="hm-rptplan-h" tabIndex={-1} ref={headingRef}>
        Workbook plan · {plan.title}
      </div>
      <div className="hm-rptplan-sum">{plan.plain_summary}</div>
      {(plan.exclusions || []).length > 0 && (
        <div className="hm-rptplan-excl">
          Leaving out: {plan.exclusions.join('; ')}
        </div>
      )}
      <details className="hm-rptplan-detail">
        <summary>Full detail</summary>
        {(plan.coverage_detail_notes || []).length > 0 && (
          <ul className="hm-rptplan-notes hm-rptplan-notes-detail"
            data-testid="hm-report-plan-coverage-detail">
            {plan.coverage_detail_notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        )}
        <div className="hm-rptplan-meta">
          {plan.window_mode === 'fixed'
            ? `Fixed window ${plan.window}`
            : `Rolling window ${(plan.window || '').replace(/_/g, ' ')}`}
          {plan.as_of ? ` · as of ${plan.as_of}` : ''}
          {` · plan v${plan.version}`}
        </div>
      </details>
      {state === 'superseded' ? (
        <div className="hm-rptplan-note" data-testid="hm-report-plan-stale">
          This plan was updated. Use the newest plan card below.
        </div>
      ) : (
        <button
          type="button"
          className="hm-rptplan-build"
          data-testid="hm-report-plan-build"
          disabled={state === 'busy'}
          aria-busy={state === 'busy'}
          onClick={build}
        >
          {state === 'busy' ? 'Starting…' : 'Build workbook'}
        </button>
      )}
      {state === 'failed' && (
        <div className="hm-rptplan-note" role="alert">{failMessage}</div>
      )}
    </div>
  );
}

const ACTIVE_STATUSES = ['queued', 'running', 'validating', 'publishing'];

/** Lane 1398: the ONE mapping from a chat response to the message's
 *  workbook fields, used by every path that appends an answer (a normal
 *  send and the "Build your workbook" click), so a response that already
 *  started a job always renders the job row and never a second Build card.
 *  `reportStart` is the recorded request to retry when the start failed. */
export function projectReportResult(data) {
  const job = data && data.report_job;
  const start = data && data.report_start;
  return {
    reportOffer: (data && data.report_offer) || null,
    // Lane 1405 v3: false = the quiet offer on a data answer (the server said so);
    // true or absent = keep showing it.
    ...(data && typeof data.report_offer_explicit === 'boolean'
      ? { reportOfferExplicit: data.report_offer_explicit } : {}),
    reportPlan: (data && data.report_plan) || null,
    ...(job && job.job_id ? { reportJobId: job.job_id } : {}),
    ...(start && start.report_operation_id
      ? { reportStart: start } : {}),
  };
}

/** Lane 1398 Phase B: the two choices after a workbook is built. Typing
 *  stays locked until one is clicked (Ryan, 2026-10-07). */
export function WorkbookChoiceBar({ onChange, onOther }) {
  return (
    <div className="hm-wbchoice" data-testid="hm-workbook-choice"
      role="group" aria-label="What next for this workbook">
      <button type="button" className="hm-rptoffer-btn" onClick={onChange}>
        Change this workbook
      </button>
      <button type="button" className="hm-rptoffer-btn" onClick={onOther}>
        Ask about something else
      </button>
    </div>
  );
}

/** Lane 1398 Phase B: the lock must never trap the buyer. Any render error
 *  in the choice bar resets the chat to normal typing. */
export class WorkbookChoiceBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    if (this.props.onReset) this.props.onReset();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** Lane 1398: Try again after the one-click start could not begin. Starts
 *  (or replays) the SAME recorded request, so a retry never builds a
 *  second workbook. */
export function ReportStartRetry({ reportStart, authFetch, onStarted }) {
  const [state, setState] = useState('idle'); // idle | busy | failed
  const [message, setMessage] = useState('');
  const start = async () => {
    if (state === 'busy') return;
    setState('busy');
    setMessage('');
    try {
      const response = await authFetch(
        `${API_BASE}/api/report-operations/`
        + `${encodeURIComponent(reportStart.report_operation_id)}/start`,
        { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setState('failed');
        setMessage((data && data.detail && data.detail.message)
          || 'The workbook still could not start.');
        return;
      }
      onStarted(data.job_id);
    } catch (e) {
      setState('failed');
      setMessage('The workbook still could not start.');
    }
  };
  return (
    <div className="hm-rptoffer" data-testid="hm-report-start-retry">
      <button type="button" className="hm-rptoffer-btn" onClick={start}
        disabled={state === 'busy'}>
        {state === 'busy' ? 'Starting…' : 'Try again'}
      </button>
      {message && <span className="hm-rptoffer-fail" role="alert">
        {message}</span>}
    </div>
  );
}

const PHASE_TEXT = {
  queued: 'Queued…',
  running: 'Building: pulling your data…',
  validating: 'Checking the file against its own numbers…',
  publishing: 'Saving to Reports…',
};

export function ReportJobRow({ jobId, authFetch, initialJob, onReady,
                              onChangeRetry }) {
  const [currentJobId, setCurrentJobId] = useState(jobId);
  const [job, setJob] = useState(initialJob || null);
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();
  const timerRef = useRef(null);
  // Lane 1398 Phase B: tell the chat ONCE that this workbook is ready (the
  // chat then offers Change this workbook / Ask about something else).
  // Rehydrated rows pass no onReady, so they never lock the chat.
  const readyReportedRef = useRef(null);
  useEffect(() => {
    if (!onReady || !job || job.status !== 'ready') return;
    if (readyReportedRef.current === job.job_id) return;
    readyReportedRef.current = job.job_id;
    onReady(job);
  }, [job, onReady]);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const response = await authFetch(`/api/report-jobs/${currentJobId}`);
        if (!response.ok) {
          // Transient (the backend's own DB-blip path is a 503): keep the
          // last known state AND keep polling at the error cadence — a
          // single non-2xx must never freeze an active job row forever
          // (Consult #5 blocking finding).
          if (!cancelled) timerRef.current = setTimeout(poll, 5000);
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        setJob(data);
        if (ACTIVE_STATUSES.includes(data.status)) {
          const delay = document.visibilityState === 'hidden' ? 5000 : 2000;
          timerRef.current = setTimeout(poll, delay);
        }
      } catch (e) {
        if (!cancelled) timerRef.current = setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentJobId, authFetch]);

  const download = async () => {
    setNotice('');
    try {
      const response = await authFetch(
        `/api/reports/${job.report_id}/download`);
      if (!response.ok) {
        setNotice('The file could not be downloaded right now.');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = serverFilename(response);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice('The file could not be downloaded right now.');
    }
  };

  const retry = async () => {
    try {
      const response = await authFetch(
        `/api/report-jobs/${currentJobId}/retry`,
        { method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idempotency_key: `retry-${currentJobId}-${Date.now()}` }) });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.job_id) {
        setCurrentJobId(data.job_id);
        setJob({ job_id: data.job_id, status: 'queued',
                 is_initiator: true });
      } else {
        setNotice(data?.detail?.message || 'Retry did not start.');
      }
    } catch (e) {
      setNotice('Retry did not start.');
    }
  };

  const cancel = async () => {
    // Compact confirmation naming that no file will publish (WS7).
    // eslint-disable-next-line no-alert
    if (!window.confirm('Cancel this workbook build? No file will be '
        + 'created.')) return;
    try {
      const response = await authFetch(
        `/api/report-jobs/${currentJobId}/cancel`, { method: 'POST' });
      if (response.ok) {
        setJob((prev) => ({ ...prev, status: 'cancelled' }));
      }
    } catch (e) { /* the poll shows the truth */ }
  };

  if (!job) {
    return (
      <div className="hm-rptjob" data-testid="hm-report-job"
        aria-busy="true">Queued…</div>
    );
  }

  const terminal = ['ready', 'failed', 'cancelled'].includes(job.status);
  const announceId = `report-job:${job.job_id || currentJobId}:terminal`;
  const shouldAnnounce = terminal && !announcedTerminals.has(announceId);
  if (shouldAnnounce) announcedTerminals.add(announceId);

  return (
    <div className="hm-rptjob" data-testid="hm-report-job"
      data-job-status={job.status}>
      <div aria-live="polite">
        {shouldAnnounce && job.status === 'ready'
          && 'Your workbook is ready.'}
        {shouldAnnounce && job.status === 'failed'
          && 'The workbook build failed.'}
      </div>
      {ACTIVE_STATUSES.includes(job.status) && (
        <span className="hm-rptjob-phase" aria-busy="true">
          {PHASE_TEXT[job.status] || 'Working…'}
          {job.is_initiator && job.status !== 'publishing' && (
            <button type="button" className="hm-rptjob-cancel"
              data-testid="hm-report-job-cancel" onClick={cancel}>
              Cancel
            </button>
          )}
        </span>
      )}
      {job.status === 'ready' && (
        <span className="hm-rptjob-ready">
          Your workbook is ready.
          <button type="button" className="hm-rptjob-dl"
            data-testid="hm-report-job-download" onClick={download}>
            Download
          </button>
          <button type="button" className="hm-rptjob-view"
            onClick={() => navigate('/reports')}>
            View in Reports
          </button>
        </span>
      )}
      {job.status === 'failed' && (
        <span className="hm-rptjob-fail">
          {job.next_action === 'retry'
            ? 'The build hit a temporary problem.'
            : 'The build could not produce a correct file.'}
          {job.can_retry && (
            <button type="button" className="hm-rptjob-retry"
              data-testid="hm-report-job-retry" onClick={retry}>
              Retry
            </button>
          )}
          {job.next_action === 'revise' && job.change_retry
            && onChangeRetry && (
            <button type="button" className="hm-rptjob-retry"
              data-testid="hm-report-job-change-retry"
              onClick={() => onChangeRetry(job)}>
              Try the change again
            </button>
          )}
          {job.next_action === 'revise'
            && !(job.change_retry && onChangeRetry) && (
            <span className="hm-rptjob-hint">
              Ask for the workbook again in chat.
            </span>
          )}
        </span>
      )}
      {job.status === 'cancelled' && (
        <span className="hm-rptjob-hint">Build cancelled.</span>
      )}
      {notice && <span className="hm-rptjob-hint" role="alert">
        {notice}</span>}
    </div>
  );
}

/** Reload rehydration (WS3d): the CURRENT user's recent jobs across chat
 *  conversation ids, fetched once on mount, so a buyer who reloads
 *  mid-build does not lose sight of the build.
 *
 *  ADOPTION IS ACTIVE-ONLY (Lane 1150). `mine=true` deliberately crosses
 *  conversation ids and the endpoint applies no status filter, so before
 *  this filter EVERY recent job the user owned rendered on a fresh chat —
 *  a stack of "Your workbook is ready." rows over the input box, seen on
 *  staging 2026-09-03. A FINISHED workbook belongs in the Reports tab,
 *  which is where the row's own "View in Reports" already goes.
 *
 *  The filter is on what is ADOPTED from the fetch, never on what a
 *  mounted row may display: a job adopted while active keeps its row when
 *  it reaches ready/failed, so a buyer watching a build finish sees it
 *  finish and gets the Download button. `adoptedIds` makes that survive a
 *  knownKey refetch too, which would otherwise re-filter the finished job
 *  out from under them. */
export function ReportJobRehydrator({ authFetch, knownJobIds }) {
  const [jobs, setJobs] = useState([]);
  // Jobs this rehydrator has already taken responsibility for. Once
  // adopted, a job is retained through its own completion.
  const adoptedIds = useRef(new Set());
  // knownJobIds arrives as a FRESH array every parent render; keying the
  // effect on the array identity refired the fetch on every render and
  // hammered the server (measured live on staging, 2026-09-01). Key on
  // the stable string identity instead.
  const knownKey = (knownJobIds || []).slice().sort().join('|');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/report-jobs?mine=true&limit=50');
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const known = new Set(
          knownKey ? knownKey.split('|').map(Number) : []);
        // A message-carried job row already renders and polls this job —
        // rendering it again here would double both (Consult #5).
        const adopted = (data.items || []).filter((j) =>
          !known.has(j.job_id)
          && (ACTIVE_STATUSES.includes(j.status)
              || adoptedIds.current.has(j.job_id)));
        adopted.forEach((j) => adoptedIds.current.add(j.job_id));
        setJobs(adopted);
      } catch (e) { /* rehydration is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [authFetch, knownKey]);

  if (jobs.length === 0) return null;
  return (
    <div data-testid="hm-report-rehydrated">
      {jobs.map((job) => (
        <ReportJobRow key={job.job_id} jobId={job.job_id}
          authFetch={authFetch} initialJob={job} />
      ))}
    </div>
  );
}
